import { 
  MessageSquare, 
  Phone, 
  Video, 
  Mail, 
  Users, 
  Globe,
  Shield,
  Zap,
  BarChart3,
  Clock,
  TrendingUp,
  Settings,
  Lock,
  Mic,
  Calendar,
  FileText,
  Share2,
  Tv,
  Megaphone,
  AlertTriangle
} from 'lucide-react';
import { ProductConfigs } from './types';

export const communicationSolutionsConfigs: ProductConfigs = {
  'unified-communications-platform': {
    keyBenefits: [
      {
        icon: MessageSquare,
        title: 'Unified Communication Hub',
        description: 'Integrated platform combining voice, video, messaging, and email with seamless switching between communication channels.',
        metrics: '75% improvement in team productivity',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Enterprise Collaboration Suite',
        description: 'Advanced collaboration tools with file sharing, screen sharing, virtual whiteboards, and real-time document editing.',
        metrics: '60% faster project completion',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Global Communication Network',
        description: 'Worldwide connectivity with multi-language support, international calling, and regional compliance management.',
        metrics: 'Connect 200+ countries instantly',
        color: 'from-purple-500 to-pink-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Powered Communication Intelligence',
        description: 'Machine learning algorithms provide real-time transcription, sentiment analysis, and automated meeting summaries.',
        metrics: '90% accuracy in speech recognition',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Advanced Analytics Dashboard',
        description: 'Comprehensive communication analytics with usage patterns, productivity metrics, and team collaboration insights.',
        metrics: '40% improvement in communication efficiency',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Workflow Automation Engine',
        description: 'Intelligent automation for routine communications, meeting scheduling, and follow-up task management.',
        metrics: '80% reduction in administrative tasks',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Enterprise-Grade Security',
        description: 'End-to-end encryption with advanced authentication, compliance monitoring, and comprehensive audit trails.',
        metrics: 'Bank-level security standards',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Clock,
        title: 'Ultra-Low Latency Communication',
        description: 'Optimised global network infrastructure with sub-100ms latency, HD quality, and 99.99% uptime guarantee.',
        metrics: '<50ms global communication latency',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Lock,
        title: 'Compliance & Data Protection',
        description: 'GDPR, HIPAA, and SOX compliant communication with data residency controls and privacy protection.',
        metrics: '100% regulatory compliance assurance',
        color: 'from-red-600 to-rose-700'
      }
    ]
  },

  'video-conferencing-solution': {
    keyBenefits: [
      {
        icon: Video,
        title: 'HD Video Conferencing',
        description: 'Crystal-clear video conferencing with 4K support, intelligent framing, and automatic quality adjustment for optimal experience.',
        metrics: '4K video quality with <100ms latency',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Large-Scale Meeting Management',
        description: 'Support for 10,000+ participants with breakout rooms, audience management, and interactive engagement tools.',
        metrics: 'Host 10,000+ participants simultaneously',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Calendar,
        title: 'Intelligent Meeting Assistant',
        description: 'AI-powered meeting assistant with automated scheduling, agenda management, and post-meeting action items.',
        metrics: '70% reduction in meeting preparation time',
        color: 'from-purple-500 to-pink-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Enhanced Meeting Experience',
        description: 'Real-time language translation, automated transcription, background noise cancellation, and virtual backgrounds.',
        metrics: 'Support for 50+ languages in real-time',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Meeting Analytics & Insights',
        description: 'Comprehensive meeting analytics with engagement metrics, participation tracking, and productivity insights.',
        metrics: '85% improvement in meeting effectiveness',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Global Infrastructure Network',
        description: 'Worldwide server network with edge computing, regional data centers, and intelligent routing for optimal performance.',
        metrics: '99.99% global uptime guarantee',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Advanced Security Protocols',
        description: 'End-to-end encryption with waiting rooms, meeting passwords, and comprehensive security controls.',
        metrics: '256-bit AES encryption standard',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Clock,
        title: 'High-Performance Streaming',
        description: 'Adaptive bitrate streaming with CDN optimisation, automatic failover, and intelligent bandwidth management.',
        metrics: '<2-second connection time globally',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Lock,
        title: 'Enterprise Data Protection',
        description: 'SOC 2 Type II certified with data loss prevention, compliance reporting, and granular access controls.',
        metrics: '100% data protection compliance',
        color: 'from-red-600 to-rose-700'
      }
    ]
  },

  'team-messaging-platform': {
    keyBenefits: [
      {
        icon: MessageSquare,
        title: 'Advanced Team Messaging',
        description: 'Real-time messaging with threaded conversations, file sharing, emoji reactions, and powerful search capabilities.',
        metrics: '90% faster team communication',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Channel Management System',
        description: 'Organised communication with public/private channels, guest access, and project-based team organisation.',
        metrics: '300+ channels per workspace support',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Settings,
        title: 'Workflow Integration Hub',
        description: 'Seamless integration with 1000+ business tools, automated workflows, and custom bot development.',
        metrics: 'Connect with 1000+ business applications',
        color: 'from-purple-500 to-pink-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Powered Smart Features',
        description: 'Intelligent message prioritisation, automated responses, smart notifications, and contextual suggestions.',
        metrics: '60% reduction in notification noise',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Team Productivity Analytics',
        description: 'Comprehensive team insights with communication patterns, response times, and collaboration effectiveness metrics.',
        metrics: '45% improvement in team efficiency',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Enterprise Knowledge Management',
        description: 'Advanced search capabilities with knowledge base integration, conversation archiving, and institutional memory preservation.',
        metrics: '80% faster information retrieval',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Enterprise Security Standards',
        description: 'End-to-end encryption with enterprise key management, compliance monitoring, and advanced threat protection.',
        metrics: 'SOC 2 Type II certified security',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Clock,
        title: 'Real-Time Message Delivery',
        description: 'Instant message delivery with offline sync, message ordering, and guaranteed delivery confirmation.',
        metrics: '<10ms message delivery globally',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Lock,
        title: 'Data Governance & Compliance',
        description: 'GDPR compliant with data retention policies, e-discovery support, and comprehensive audit logging.',
        metrics: '100% compliance with data regulations',
        color: 'from-red-600 to-rose-700'
      }
    ]
  },

  'voice-over-ip-system': {
    keyBenefits: [
      {
        icon: Phone,
        title: 'Advanced VoIP Technology',
        description: 'Crystal-clear voice communication with HD audio, intelligent call routing, and advanced telephony features.',
        metrics: 'HD audio quality with <30ms latency',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Globe,
        title: 'Global Calling Network',
        description: 'Worldwide calling capabilities with competitive international rates, local number provisioning, and global presence.',
        metrics: 'Connect to 200+ countries instantly',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Settings,
        title: 'Enterprise Phone System',
        description: 'Complete business phone solution with auto attendants, call queues, voicemail-to-email, and mobile integration.',
        metrics: '99.9% call completion rate',
        color: 'from-purple-500 to-pink-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Enhanced Call Management',
        description: 'Intelligent call routing with sentiment analysis, automated transcription, and predictive call analytics.',
        metrics: '85% improvement in call handling efficiency',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Advanced Call Analytics',
        description: 'Comprehensive call reporting with quality metrics, performance dashboards, and business intelligence insights.',
        metrics: '95% call quality assurance',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Mic,
        title: 'Voice Recognition & Processing',
        description: 'Advanced voice processing with noise cancellation, echo suppression, and real-time voice enhancement.',
        metrics: '99% voice recognition accuracy',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Secure Voice Communications',
        description: 'End-to-end encrypted voice calls with SRTP protocols, secure signalling, and comprehensive fraud protection.',
        metrics: 'Military-grade voice encryption',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Clock,
        title: 'High-Availability Infrastructure',
        description: 'Redundant global infrastructure with automatic failover, load balancing, and 99.99% uptime guarantee.',
        metrics: '99.99% service availability',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Lock,
        title: 'Regulatory Compliance',
        description: 'HIPAA, SOX, and industry-specific compliance with call recording, audit trails, and data protection.',
        metrics: '100% regulatory compliance assurance',
        color: 'from-red-600 to-rose-700'
      }
    ]
  },

  'video-conferencing-platform': {
    keyBenefits: [
      {
        icon: Video,
        title: 'Enterprise Video Communications',
        description: 'Professional-grade video conferencing with HD quality, screen sharing, recording, and advanced collaboration features.',
        metrics: 'Support 1000+ concurrent participants',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Advanced Collaboration Tools',
        description: 'Comprehensive collaboration suite with virtual backgrounds, breakout rooms, whiteboarding, and real-time annotation.',
        metrics: '90% improvement in meeting engagement',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Calendar,
        title: 'Integrated Scheduling System',
        description: 'Seamless calendar integration with automated meeting setup, participant management, and reminder notifications.',
        metrics: '70% reduction in scheduling time',
        color: 'from-purple-500 to-pink-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Powered Meeting Intelligence',
        description: 'Intelligent features including real-time transcription, automated summaries, and smart participant insights.',
        metrics: '95% transcription accuracy across languages',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Meeting Analytics & Reporting',
        description: 'Comprehensive analytics with engagement metrics, attendance tracking, and productivity performance insights.',
        metrics: '85% increase in meeting effectiveness',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Global Infrastructure Network',
        description: 'Worldwide server distribution with edge computing, regional optimization, and guaranteed service availability.',
        metrics: '99.99% uptime with <50ms latency',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Enterprise Security Standards',
        description: 'Bank-grade security with end-to-end encryption, waiting rooms, meeting passwords, and comprehensive access controls.',
        metrics: '256-bit AES encryption with SOC 2 compliance',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Clock,
        title: 'High-Performance Streaming',
        description: 'Adaptive streaming technology with intelligent bandwidth management, automatic quality adjustment, and CDN optimization.',
        metrics: '<3-second connection time globally',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Lock,
        title: 'Data Protection & Compliance',
        description: 'GDPR, HIPAA compliant with data residency controls, automatic data retention, and comprehensive audit logging.',
        metrics: '100% compliance with global regulations',
        color: 'from-red-600 to-rose-700'
      }
    ]
  },

  'team-chat-messaging': {
    keyBenefits: [
      {
        icon: MessageSquare,
        title: 'Unified Team Communication',
        description: 'Comprehensive messaging platform with channels, direct messages, file sharing, and seamless integration capabilities.',
        metrics: '80% faster team response times',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Advanced Channel Management',
        description: 'Sophisticated channel organisation with public/private channels, guest access, and project-based team structures.',
        metrics: 'Unlimited channels with 50K+ members',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Settings,
        title: 'Workflow Integration Hub',
        description: 'Seamless integration with 2000+ business applications, custom workflows, and intelligent automation capabilities.',
        metrics: 'Connect with 2000+ business tools',
        color: 'from-purple-500 to-pink-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Enhanced Communication',
        description: 'Intelligent features including smart notifications, message prioritization, automated responses, and contextual suggestions.',
        metrics: '65% reduction in communication overhead',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Team Productivity Analytics',
        description: 'Advanced analytics with communication patterns, response time analysis, and team collaboration effectiveness metrics.',
        metrics: '50% improvement in team coordination',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Knowledge Management System',
        description: 'Advanced search with conversation archiving, knowledge base integration, and institutional memory preservation.',
        metrics: '90% faster information discovery',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Enterprise-Grade Security',
        description: 'End-to-end encryption with enterprise key management, advanced threat protection, and comprehensive security monitoring.',
        metrics: 'SOC 2 Type II + ISO 27001 certified',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Clock,
        title: 'Real-Time Message Delivery',
        description: 'Instant message delivery with offline synchronization, message ordering guarantee, and delivery confirmation.',
        metrics: '<5ms message delivery worldwide',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Lock,
        title: 'Compliance & Data Governance',
        description: 'GDPR, HIPAA compliant with data retention policies, e-discovery support, and comprehensive audit trails.',
        metrics: '100% regulatory compliance guarantee',
        color: 'from-red-600 to-rose-700'
      }
    ]
  },

  'document-collaboration-suite': {
    keyBenefits: [
      {
        icon: FileText,
        title: 'Real-Time Document Collaboration',
        description: 'Simultaneous multi-user editing with real-time co-authoring, version control, and intelligent conflict resolution.',
        metrics: '95% improvement in document collaboration',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Advanced Review & Comment System',
        description: 'Sophisticated review workflows with threaded comments, approval processes, and collaborative decision-making tools.',
        metrics: '80% faster document approval cycles',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Settings,
        title: 'Template & Automation Engine',
        description: 'Extensive template library with automated formatting, smart suggestions, and workflow-driven document creation.',
        metrics: '70% reduction in document creation time',
        color: 'from-purple-500 to-pink-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Powered Document Intelligence',
        description: 'Machine learning features including smart formatting, content suggestions, grammar checking, and automated summarization.',
        metrics: '90% improvement in document quality',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Document Analytics & Insights',
        description: 'Comprehensive document analytics with usage patterns, collaboration metrics, and productivity performance insights.',
        metrics: '85% increase in content effectiveness',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Cross-Platform Accessibility',
        description: 'Universal access across devices with offline editing capabilities, mobile optimization, and seamless synchronization.',
        metrics: '99.9% cross-platform compatibility',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Document Security & Protection',
        description: 'Advanced security with granular permissions, document encryption, watermarking, and comprehensive access controls.',
        metrics: 'Bank-level document security standards',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Clock,
        title: 'High-Performance Editing',
        description: 'Optimized editing engine with instant synchronization, conflict-free collaborative editing, and real-time updates.',
        metrics: '<100ms collaborative editing latency',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Lock,
        title: 'Compliance & Data Protection',
        description: 'GDPR, HIPAA compliant with document retention policies, audit trails, and comprehensive data governance.',
        metrics: '100% regulatory compliance assurance',
        color: 'from-red-600 to-rose-700'
      }
    ]
  },

  'screen-sharing-remote-access': {
    keyBenefits: [
      {
        icon: Share2,
        title: 'Advanced Screen Sharing Technology',
        description: 'High-definition screen sharing with multi-monitor support, selective area sharing, and real-time annotation capabilities.',
        metrics: '4K screen sharing with <50ms latency',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Secure Remote Access Platform',
        description: 'Enterprise-grade remote desktop access with file transfer, session recording, and comprehensive remote control features.',
        metrics: '99.9% secure connection reliability',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Settings,
        title: 'Flexible Access Management',
        description: 'Granular permission controls with unattended access, scheduled sessions, and multi-user collaboration support.',
        metrics: '24/7 unattended access capability',
        color: 'from-purple-500 to-pink-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Enhanced Remote Support',
        description: 'Intelligent remote assistance with automated diagnostics, predictive issue detection, and smart troubleshooting guides.',
        metrics: '75% faster issue resolution time',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Session Analytics & Reporting',
        description: 'Comprehensive session analytics with usage tracking, performance metrics, and detailed audit reporting capabilities.',
        metrics: '90% improvement in support efficiency',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Cross-Platform Compatibility',
        description: 'Universal compatibility across operating systems with mobile access, web-based connections, and seamless integration.',
        metrics: 'Support for 15+ platforms and devices',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Military-Grade Security',
        description: 'End-to-end encryption with advanced authentication, secure tunneling, and comprehensive threat protection.',
        metrics: '256-bit AES encryption with zero-trust',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Clock,
        title: 'High-Performance Streaming',
        description: 'Optimized streaming protocols with adaptive quality, bandwidth optimization, and ultra-low latency connections.',
        metrics: '<30ms global connection latency',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Lock,
        title: 'Compliance & Data Protection',
        description: 'SOC 2, HIPAA compliant with comprehensive audit trails, data loss prevention, and regulatory compliance monitoring.',
        metrics: '100% compliance with security standards',
        color: 'from-red-600 to-rose-700'
      }
    ]
  },

  'voip-system': {
    keyBenefits: [
      {
        icon: Phone,
        title: 'Enterprise VoIP Communication',
        description: 'Professional cloud-based phone system with advanced call routing, voicemail transcription, and mobile integration.',
        metrics: 'HD voice quality with 99.9% uptime',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Globe,
        title: 'Global Telephony Network',
        description: 'Worldwide calling capabilities with competitive international rates, local number provisioning, and global coverage.',
        metrics: 'Connect to 200+ countries with local rates',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Settings,
        title: 'Advanced Call Management',
        description: 'Sophisticated call handling with auto attendants, call queues, conference calling, and CRM integration.',
        metrics: '85% improvement in call handling efficiency',
        color: 'from-purple-500 to-pink-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Powered Call Intelligence',
        description: 'Intelligent call features including sentiment analysis, automated transcription, call scoring, and predictive analytics.',
        metrics: '92% accuracy in call transcription',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Comprehensive Call Analytics',
        description: 'Advanced reporting with call quality metrics, performance dashboards, and detailed business intelligence insights.',
        metrics: '95% call quality monitoring accuracy',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Mic,
        title: 'Advanced Voice Processing',
        description: 'Professional voice enhancement with noise cancellation, echo suppression, and real-time audio optimization.',
        metrics: '99% crystal-clear voice quality',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Secure Voice Communications',
        description: 'Enterprise-grade security with encrypted voice calls, secure signaling protocols, and comprehensive fraud protection.',
        metrics: 'Military-grade voice encryption standards',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Clock,
        title: 'High-Availability Infrastructure',
        description: 'Redundant global network with automatic failover, intelligent load balancing, and guaranteed service availability.',
        metrics: '99.99% service availability guarantee',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Lock,
        title: 'Regulatory Compliance',
        description: 'HIPAA, SOX compliant with call recording capabilities, comprehensive audit trails, and data protection controls.',
        metrics: '100% regulatory compliance assurance',
        color: 'from-red-600 to-rose-700'
      }
    ]
  },

  'digital-signage-platform': {
    keyBenefits: [
      {
        icon: Tv,
        title: 'Dynamic Content Management',
        description: 'Comprehensive digital signage solution with real-time content updates, scheduled publishing, and multi-location display management.',
        metrics: 'Manage 10,000+ displays simultaneously',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Interactive Display Solutions',
        description: 'Advanced interactive capabilities with touch screen support, audience analytics, and personalized content delivery.',
        metrics: '85% increase in audience engagement',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Settings,
        title: 'Professional Template Library',
        description: 'Extensive collection of professionally designed templates with customisation tools and brand consistency features.',
        metrics: '500+ professional templates available',
        color: 'from-purple-500 to-pink-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Driven Content Optimization',
        description: 'Machine learning algorithms optimize content delivery based on audience analytics, engagement patterns, and performance metrics.',
        metrics: '75% improvement in content effectiveness',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Advanced Analytics Dashboard',
        description: 'Comprehensive analytics with audience demographics, engagement metrics, and detailed performance insights.',
        metrics: '95% accuracy in audience analytics',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Cloud-Based Management',
        description: 'Centralized cloud platform with remote monitoring, automatic updates, and seamless content distribution worldwide.',
        metrics: 'Global content delivery in <2 seconds',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Enterprise Security Standards',
        description: 'Advanced security with encrypted content delivery, secure device management, and comprehensive access controls.',
        metrics: '256-bit encryption with SOC 2 compliance',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Clock,
        title: 'High-Performance Display',
        description: 'Optimized content delivery with adaptive streaming, bandwidth management, and guaranteed display reliability.',
        metrics: '99.9% display uptime guarantee',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Lock,
        title: 'Data Protection & Privacy',
        description: 'GDPR compliant with privacy controls, data anonymization, and comprehensive audience data protection.',
        metrics: '100% privacy compliance assurance',
        color: 'from-red-600 to-rose-700'
      }
    ]
  },

  'event-management-system': {
    keyBenefits: [
      {
        icon: Calendar,
        title: 'Complete Event Coordination',
        description: 'End-to-end event management with registration, ticketing, attendee management, and comprehensive event planning tools.',
        metrics: 'Handle 100,000+ attendees per event',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Advanced Attendee Experience',
        description: 'Sophisticated attendee management with personalized experiences, networking features, and interactive engagement tools.',
        metrics: '90% attendee satisfaction improvement',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Settings,
        title: 'Integrated Payment Solutions',
        description: 'Comprehensive payment processing with multiple payment methods, refund management, and financial reporting.',
        metrics: '99.9% payment processing reliability',
        color: 'from-purple-500 to-pink-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Powered Event Intelligence',
        description: 'Machine learning features including predictive analytics, automated recommendations, and intelligent resource allocation.',
        metrics: '80% improvement in event planning efficiency',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Comprehensive Event Analytics',
        description: 'Advanced analytics with attendee behavior insights, engagement metrics, and detailed ROI analysis.',
        metrics: '95% accuracy in event performance metrics',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Hybrid Event Management',
        description: 'Seamless hybrid event support with live streaming integration, virtual networking, and unified attendee experience.',
        metrics: 'Support for unlimited virtual attendees',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Enterprise Event Security',
        description: 'Advanced security with encrypted data handling, secure payment processing, and comprehensive access controls.',
        metrics: 'PCI DSS compliant payment security',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Clock,
        title: 'High-Performance Platform',
        description: 'Scalable infrastructure with load balancing, automatic scaling, and guaranteed platform availability during events.',
        metrics: '99.99% platform availability during events',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Lock,
        title: 'Data Privacy & Compliance',
        description: 'GDPR, CCPA compliant with comprehensive data protection, privacy controls, and attendee data security.',
        metrics: '100% compliance with privacy regulations',
        color: 'from-red-600 to-rose-700'
      }
    ]
  },

  'survey-feedback-platform': {
    keyBenefits: [
      {
        icon: BarChart3,
        title: 'Advanced Survey Builder',
        description: 'Sophisticated survey creation with multiple question types, logic branching, and professional design templates.',
        metrics: '50+ question types and logic options',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Multi-Channel Distribution',
        description: 'Comprehensive distribution channels including email, SMS, social media, QR codes, and embedded surveys.',
        metrics: '95% response rate improvement',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Settings,
        title: 'Advanced Data Analytics',
        description: 'Powerful analytics engine with real-time reporting, data visualization, and statistical analysis capabilities.',
        metrics: 'Real-time insights with 40+ chart types',
        color: 'from-purple-500 to-pink-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Powered Survey Intelligence',
        description: 'Machine learning features including sentiment analysis, predictive insights, and automated survey optimization.',
        metrics: '85% improvement in survey effectiveness',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Advanced Analytics Dashboard',
        description: 'Comprehensive analytics with trend analysis, cross-tabulation, and advanced statistical reporting capabilities.',
        metrics: '90% faster insight generation',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Multi-Language Support',
        description: 'Global survey capabilities with 50+ language support, cultural adaptation, and international compliance.',
        metrics: 'Support for 50+ languages worldwide',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Enterprise Data Security',
        description: 'Advanced security with data encryption, anonymous responses, and comprehensive privacy protection controls.',
        metrics: '256-bit encryption with anonymous options',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Clock,
        title: 'High-Performance Platform',
        description: 'Scalable infrastructure with fast response collection, real-time processing, and guaranteed platform reliability.',
        metrics: '99.9% platform uptime guarantee',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Lock,
        title: 'Privacy & Compliance',
        description: 'GDPR, HIPAA compliant with comprehensive data protection, consent management, and regulatory compliance.',
        metrics: '100% compliance with privacy regulations',
        color: 'from-red-600 to-rose-700'
      }
    ]
  },

  'internal-communications-hub': {
    keyBenefits: [
      {
        icon: Megaphone,
        title: 'Centralized Communication Platform',
        description: 'Unified internal communications with company announcements, newsletters, employee directories, and engagement tools.',
        metrics: '85% improvement in employee engagement',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Employee Engagement Suite',
        description: 'Comprehensive engagement tools with recognition systems, pulse surveys, social feeds, and community building features.',
        metrics: '90% employee participation rate',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Settings,
        title: 'Content Management System',
        description: 'Advanced content management with approval workflows, content targeting, scheduling, and multi-format publishing.',
        metrics: '75% reduction in content management time',
        color: 'from-purple-500 to-pink-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Powered Content Intelligence',
        description: 'Machine learning features including content recommendations, engagement prediction, and automated personalization.',
        metrics: '80% improvement in content engagement',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Advanced Analytics Dashboard',
        description: 'Comprehensive analytics with employee engagement metrics, content performance insights, and communication effectiveness.',
        metrics: '95% accuracy in engagement analytics',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Multi-Location Support',
        description: 'Global communication capabilities with multi-location targeting, timezone management, and cultural customisation.',
        metrics: 'Support for unlimited global locations',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Enterprise Security Standards',
        description: 'Advanced security with role-based access controls, content approval workflows, and comprehensive audit trails.',
        metrics: 'SOC 2 Type II security compliance',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Clock,
        title: 'High-Performance Platform',
        description: 'Scalable infrastructure with fast content delivery, real-time notifications, and guaranteed platform availability.',
        metrics: '99.9% platform uptime with instant delivery',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Lock,
        title: 'Data Privacy & Protection',
        description: 'GDPR compliant with employee data protection, privacy controls, and comprehensive data governance.',
        metrics: '100% compliance with privacy regulations',
        color: 'from-red-600 to-rose-700'
      }
    ]
  },

  'crisis-communication-system': {
    keyBenefits: [
      {
        icon: AlertTriangle,
        title: 'Emergency Communication Platform',
        description: 'Critical crisis communication with multi-channel emergency alerts, geo-targeted notifications, and incident management.',
        metrics: 'Deliver alerts to 1M+ people in <60 seconds',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Mass Notification System',
        description: 'Comprehensive mass communication with voice, SMS, email, push notifications, and social media broadcasting.',
        metrics: '99.9% message delivery reliability',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Settings,
        title: 'Incident Management Suite',
        description: 'Complete incident management with escalation procedures, two-way communication, and real-time response tracking.',
        metrics: '85% faster emergency response time',
        color: 'from-purple-500 to-pink-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Powered Crisis Intelligence',
        description: 'Machine learning for threat detection, automated response triggers, and intelligent communication routing.',
        metrics: '90% improvement in threat detection speed',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Real-Time Analytics Dashboard',
        description: 'Comprehensive crisis analytics with response tracking, communication effectiveness, and detailed incident reporting.',
        metrics: '95% accuracy in response analytics',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Global Crisis Management',
        description: 'Worldwide crisis communication with multi-language support, regional compliance, and international emergency protocols.',
        metrics: 'Global coverage with <30-second alerts',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Mission-Critical Security',
        description: 'Military-grade security with redundant systems, encrypted communications, and comprehensive threat protection.',
        metrics: '256-bit encryption with 99.99% uptime',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Clock,
        title: 'Ultra-High Availability',
        description: 'Redundant global infrastructure with automatic failover, load balancing, and guaranteed emergency availability.',
        metrics: '99.99% availability during emergencies',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Lock,
        title: 'Regulatory Compliance',
        description: 'FEMA, FCC compliant with emergency communication standards, audit trails, and regulatory reporting.',
        metrics: '100% compliance with emergency standards',
        color: 'from-red-600 to-rose-700'
      }
    ]
  }
};